// ════════════════════════════════════════════════════════════════════════════
// FLIGHTS — the pillar's RESULT SHAPE. The pillar that decides the ARRAY
// question, because an itinerary IS an ordered array of legs.
//
// ─── HOW AN ORDERED SUB-ARRAY IS CARRIED, AND WHY BOTH ANSWERS ARE RIGHT ────
//
// Ruled earlier, under the untyped design: an ordered sub-array is a REPEATED
// KEY, because the order the lines print in is the order the legs fly in. That
// ruling stands ON THE WIRE and nothing about it changes here — three
// `segment:` lines are still a three-leg itinerary, in order.
//
// What the typed re-cut adds is the other half: the READ of those repeated
// lines is `segments: FlightsAttachSegment[]`, a real array of real records.
// The two are not alternatives — one is the printed form, the other is what a
// widget receives — and taking the array in the typed shape is exactly the
// permission the ruling left open, since the objection to an array field was
// that an untyped `{key, value}` bag could not express one.
//
// ─── A LEG IS A RECORD, SO IT IS A SUB-RECORD LINE ──────────────────────────
//
// One leg carries ten named values. Folded into a ` · ` prose string — which
// is what this pillar did — a widget could not get the departure airport out
// of it without splitting text. So the value of a `segment:` line is a
// SUB-RECORD in the grammar's own `key: value` shape, joined by the map's own
// ` :: `, parsed by the shared codec and never by a consumer.
//
// The fold is SAFE here for the reason the pillar's own census already proved:
// this item prints NO free text at all. Every part is a closed vendor
// vocabulary — an airport code, an ISO date, a 24-hour clock, an `XX NNN`
// flight number, a cabin name, an aircraft name, a legroom measurement, a wifi
// word, a number, a flag. That is D21 applied to a fold one level down, and
// `attachSubRecordSafe` is its mechanical check.
//
// ─── CENSUS (D28), UNCHANGED FROM `flights.attach.ts` ───────────────────────
//
// 14 itineraries / 26 segments, one capture (LIS→JFK 2026-10-15, 1 adult):
// price 14/14 · currency 14/14 · type 14/14 · totalDurationMinutes 14/14 ·
// stopsCount 14/14 · layovers 12/14 (absent on exactly the two nonstops) ·
// carbonKg 13/14 (row 11 holds a live NaN) · segments: from/to 26/26 ·
// durationMinutes 26/26 · airline 26/26 · flightNumber 26/26 · cabin 26/26 ·
// airplane 25/26 · legroom 25/26 · wifi 20/26 · oftenDelayed 5/26 and ONLY
// ever `true`.
//
// ─── NO ADDRESS FIELD, AND THAT IS A PROOF ──────────────────────────────────
//
// Google Flights publishes no URL for an itinerary; a flights row has no
// `address.url` at all, so `bindRowCodes` can never bind a short-link code to
// one and `citationUrl` is structurally null on this pillar. The typed shape
// therefore has no address member — never an `/r/`, which is dead the moment
// the attempt ends (U1/U9, SPEC §5b).
// ════════════════════════════════════════════════════════════════════════════

import type { AttachItemBlock, AttachFieldLine } from '../grammar.js';
import { attachFieldValue, attachFieldValues } from '../parse-attach-document.js';
import {
  attachFieldGroups,
  attachFlagText,
  attachGroupValues,
  attachNumberOf,
  attachNumberText,
  attachPartFlag,
  attachPartNumber,
  attachPartText,
  attachSubRecordOf,
  attachSubRecordText,
  attachTextOf,
} from './attach-value-codec.js';

/** The tagged keys a flights item prints. Named for what the reader is looking
 *  at (D19): a `segment` is one leg he sits through, a `layover` is the wait
 *  between two, a `fare` is a price someone will actually sell him. */
export enum FlightsAttachField {
  /** The whole-itinerary price, as a number. The currency is its own field —
   *  a bare figure on a route priced in another currency is a wrong answer. */
  Price = 'price',
  Currency = 'currency',
  /** `One way` / `Round trip`, the vendor's own word. */
  TripType = 'trip type',
  /** Door to door, in MINUTES. Hours-and-minutes is a reading of this number
   *  and is composed for display, never stored in its place. */
  DurationMinutes = 'duration minutes',
  /** How many times he changes plane. Zero is a number and prints as one —
   *  `Nonstop` is the display of it. */
  Stops = 'stops',
  /** One leg. REPEATED, and the run of them IS the itinerary, in order. */
  Segment = 'segment',
  /** One wait between two legs. REPEATED, in order. Absent entirely on a
   *  nonstop, where the vendor sends no key at all. */
  Layover = 'layover',
  /** What this itinerary burns, in kilograms of CO2e. */
  CarbonKg = 'carbon kg',
  /** How that compares with the route's typical flight, in percent. */
  CarbonVsTypicalPct = 'carbon vs typical pct',
  /** One bookable fare. DRILL ONLY, REPEATED, in the vendor's own order. Opens
   *  a GROUP: the `fare includes` and `fare bags` lines under it are its own. */
  Fare = 'fare',
  /** One thing that fare includes. REPEATED inside its fare, its own line
   *  because these are vendor sentences rather than closed tokens. */
  FareIncludes = 'fare includes',
  /** One baggage price on that fare. REPEATED inside its fare, same reason. */
  FareBags = 'fare bags',
}

/** The parts of the sub-records this pillar writes. */
export enum FlightsAttachPart {
  From = 'from',
  DepartDate = 'depart date',
  Depart = 'depart',
  To = 'to',
  ArriveDate = 'arrive date',
  Arrive = 'arrive',
  Airline = 'airline',
  Flight = 'flight',
  DurationMinutes = 'duration minutes',
  Cabin = 'cabin',
  Airplane = 'airplane',
  Legroom = 'legroom',
  Wifi = 'wifi',
  OftenDelayed = 'often delayed',
  Airport = 'airport',
  Seller = 'seller',
  Title = 'title',
  FareType = 'fare type',
  Price = 'price',
  Currency = 'currency',
}

// ─── the typed shape ────────────────────────────────────────────────────────

/** One end of one leg: where, on what day, at what clock time. */
export interface FlightsAttachEndpoint {
  /** IATA airport code. */
  readonly id: string;
  /** ISO calendar day, or null where the vendor stated none. */
  readonly date: string | null;
  /** 24-hour local clock, verbatim. */
  readonly time: string | null;
}

export interface FlightsAttachSegment {
  readonly from: FlightsAttachEndpoint;
  readonly to: FlightsAttachEndpoint;
  readonly airline: string | null;
  readonly flightNumber: string | null;
  readonly durationMinutes: number | null;
  readonly cabin: string | null;
  readonly airplane: string | null;
  readonly legroom: string | null;
  readonly wifi: string | null;
  /** Null is silence, never a claim the flight runs on time — the vendor has
   *  only ever asserted `true`. */
  readonly oftenDelayed: boolean | null;
}

export interface FlightsAttachLayover {
  readonly airportId: string;
  readonly durationMinutes: number | null;
}

export interface FlightsAttachFare {
  readonly seller: string | null;
  readonly optionTitle: string | null;
  readonly fareType: string | null;
  readonly priceAmount: number | null;
  readonly currency: string | null;
  readonly includes: readonly string[];
  readonly baggagePrices: readonly string[];
}

/** One itinerary, typed. The object a widget renders from. */
export interface FlightsAttachResult {
  readonly priceAmount: number | null;
  readonly currency: string | null;
  readonly tripType: string | null;
  readonly totalDurationMinutes: number | null;
  readonly stopsCount: number | null;
  readonly segments: readonly FlightsAttachSegment[];
  readonly layovers: readonly FlightsAttachLayover[];
  readonly carbonKg: number | null;
  readonly carbonVsTypicalPct: number | null;
  readonly fares: readonly FlightsAttachFare[];
}

// ─── display helpers, shared so both sides read a number the same way ───────

const MINUTES_PER_HOUR = 60;
const HEADLINE_SEPARATOR = ' · ';
const NONSTOP = 'Nonstop';
const ONE_STOP = '1 stop';
const STOPS_SUFFIX = ' stops';

/** A duration as hours and minutes (`8h`, `8h 30m`, `45 min`) — a reading of
 *  the stored number, exported so the FE composes the same reading the block's
 *  own headline does. A reader plans around hours; nobody plans around 775. */
export function flightsDurationText(input: { minutes: number | null }): string | null {
  const minutes = input.minutes;
  if (minutes === null || !Number.isFinite(minutes)) return null;
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;
  if (hours === 0) return `${String(rest)} min`;
  return rest === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(rest)}m`;
}

/** How many times he changes plane, in the words the shipped attach card
 *  ruled: `Nonstop`, never `0 stops`. */
export function flightsStopsText(input: { stopsCount: number | null }): string | null {
  const stops = input.stopsCount;
  if (stops === null) return null;
  if (stops === 0) return NONSTOP;
  return stops === 1 ? ONE_STOP : `${String(stops)}${STOPS_SUFFIX}`;
}

function moneyText(input: { amount: number | null; currency: string | null }): string | null {
  if (input.amount === null) return null;
  const amount = String(input.amount);
  return input.currency === null ? amount : `${input.currency} ${amount}`;
}

/**
 * The human read of one itinerary — DISPLAY ONLY, composed from the typed
 * fields and never parsed back. The route is deliberately absent: every row of
 * one block is one search's answer to one route, and the block's `about`
 * directive states it once (D17).
 */
export function flightsAttachHeadline(input: { result: FlightsAttachResult }): string {
  const result = input.result;
  return [
    moneyText({ amount: result.priceAmount, currency: result.currency }),
    flightsDurationText({ minutes: result.totalDurationMinutes }),
    flightsStopsText({ stopsCount: result.stopsCount }),
    result.tripType,
  ]
    .filter((part): part is string => part !== null && part.length > 0)
    .join(HEADLINE_SEPARATOR);
}

// ─── writing ────────────────────────────────────────────────────────────────

function textLine(input: {
  key: FlightsAttachField;
  value: string | null;
}): AttachFieldLine[] {
  const value = attachTextOf({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function numberLine(input: {
  key: FlightsAttachField;
  value: number | null;
}): AttachFieldLine[] {
  if (input.value === null) return [];
  const value = attachNumberText({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function numberPart(input: { value: number | null }): string | null {
  return input.value === null ? null : attachNumberText({ value: input.value });
}

function segmentLine(input: { segment: FlightsAttachSegment }): AttachFieldLine[] {
  const segment = input.segment;
  const value = attachSubRecordText({
    parts: [
      { key: FlightsAttachPart.From, value: segment.from.id },
      { key: FlightsAttachPart.DepartDate, value: segment.from.date },
      { key: FlightsAttachPart.Depart, value: segment.from.time },
      { key: FlightsAttachPart.To, value: segment.to.id },
      { key: FlightsAttachPart.ArriveDate, value: segment.to.date },
      { key: FlightsAttachPart.Arrive, value: segment.to.time },
      { key: FlightsAttachPart.Airline, value: segment.airline },
      { key: FlightsAttachPart.Flight, value: segment.flightNumber },
      {
        key: FlightsAttachPart.DurationMinutes,
        value: numberPart({ value: segment.durationMinutes }),
      },
      { key: FlightsAttachPart.Cabin, value: segment.cabin },
      { key: FlightsAttachPart.Airplane, value: segment.airplane },
      { key: FlightsAttachPart.Legroom, value: segment.legroom },
      { key: FlightsAttachPart.Wifi, value: segment.wifi },
      {
        key: FlightsAttachPart.OftenDelayed,
        value: segment.oftenDelayed === null
          ? null
          : attachFlagText({ value: segment.oftenDelayed }),
      },
    ],
  });
  return value === null ? [] : [{ key: FlightsAttachField.Segment, value }];
}

function fareLines(input: { fare: FlightsAttachFare }): AttachFieldLine[] {
  const fare = input.fare;
  const open = attachSubRecordText({
    parts: [
      { key: FlightsAttachPart.Seller, value: fare.seller },
      { key: FlightsAttachPart.Title, value: fare.optionTitle },
      { key: FlightsAttachPart.FareType, value: fare.fareType },
      { key: FlightsAttachPart.Price, value: numberPart({ value: fare.priceAmount }) },
      { key: FlightsAttachPart.Currency, value: fare.currency },
    ],
  });
  if (open === null) return [];
  return [
    { key: FlightsAttachField.Fare, value: open },
    ...fare.includes.flatMap((entry) =>
      textLine({ key: FlightsAttachField.FareIncludes, value: entry })),
    ...fare.baggagePrices.flatMap((entry) =>
      textLine({ key: FlightsAttachField.FareBags, value: entry })),
  ];
}

/** One typed itinerary as a block item. */
export function writeFlightsAttachItem(input: {
  result: FlightsAttachResult;
}): AttachItemBlock {
  const result = input.result;
  const fields: AttachFieldLine[] = [
    ...numberLine({ key: FlightsAttachField.Price, value: result.priceAmount }),
    ...textLine({ key: FlightsAttachField.Currency, value: result.currency }),
    ...textLine({ key: FlightsAttachField.TripType, value: result.tripType }),
    ...numberLine({
      key: FlightsAttachField.DurationMinutes,
      value: result.totalDurationMinutes,
    }),
    ...numberLine({ key: FlightsAttachField.Stops, value: result.stopsCount }),
    ...result.segments.flatMap((segment) => segmentLine({ segment })),
    ...result.layovers.flatMap((layover) => {
      const value = attachSubRecordText({
        parts: [
          { key: FlightsAttachPart.Airport, value: layover.airportId },
          {
            key: FlightsAttachPart.DurationMinutes,
            value: numberPart({ value: layover.durationMinutes }),
          },
        ],
      });
      return value === null ? [] : [{ key: FlightsAttachField.Layover, value }];
    }),
    ...numberLine({ key: FlightsAttachField.CarbonKg, value: result.carbonKg }),
    ...numberLine({
      key: FlightsAttachField.CarbonVsTypicalPct,
      value: result.carbonVsTypicalPct,
    }),
    ...result.fares.flatMap((fare) => fareLines({ fare })),
  ];
  return { headline: flightsAttachHeadline({ result }), fields };
}

// ─── reading ────────────────────────────────────────────────────────────────

function fieldText(input: { item: AttachItemBlock; key: FlightsAttachField }): string | null {
  return attachTextOf({ value: attachFieldValue({ item: input.item, key: input.key }) });
}

function fieldNumber(input: { item: AttachItemBlock; key: FlightsAttachField }): number | null {
  return attachNumberOf({ value: fieldText(input) });
}

function segmentsOf(input: { item: AttachItemBlock }): FlightsAttachSegment[] {
  return attachFieldValues({ item: input.item, key: FlightsAttachField.Segment })
    .map((value) => {
      const record = attachSubRecordOf({ value });
      return {
        from: {
          id: attachPartText({ record, key: FlightsAttachPart.From }) ?? '',
          date: attachPartText({ record, key: FlightsAttachPart.DepartDate }),
          time: attachPartText({ record, key: FlightsAttachPart.Depart }),
        },
        to: {
          id: attachPartText({ record, key: FlightsAttachPart.To }) ?? '',
          date: attachPartText({ record, key: FlightsAttachPart.ArriveDate }),
          time: attachPartText({ record, key: FlightsAttachPart.Arrive }),
        },
        airline: attachPartText({ record, key: FlightsAttachPart.Airline }),
        flightNumber: attachPartText({ record, key: FlightsAttachPart.Flight }),
        durationMinutes: attachPartNumber({
          record,
          key: FlightsAttachPart.DurationMinutes,
        }),
        cabin: attachPartText({ record, key: FlightsAttachPart.Cabin }),
        airplane: attachPartText({ record, key: FlightsAttachPart.Airplane }),
        legroom: attachPartText({ record, key: FlightsAttachPart.Legroom }),
        wifi: attachPartText({ record, key: FlightsAttachPart.Wifi }),
        oftenDelayed: attachPartFlag({ record, key: FlightsAttachPart.OftenDelayed }),
      };
    });
}

function layoversOf(input: { item: AttachItemBlock }): FlightsAttachLayover[] {
  return attachFieldValues({ item: input.item, key: FlightsAttachField.Layover })
    .flatMap((value) => {
      const record = attachSubRecordOf({ value });
      const airportId = attachPartText({ record, key: FlightsAttachPart.Airport });
      if (airportId === null) return [];
      return [
        {
          airportId,
          durationMinutes: attachPartNumber({
            record,
            key: FlightsAttachPart.DurationMinutes,
          }),
        },
      ];
    });
}

function faresOf(input: { item: AttachItemBlock }): FlightsAttachFare[] {
  return attachFieldGroups({
    item: input.item,
    openKey: FlightsAttachField.Fare,
    memberKeys: [FlightsAttachField.FareIncludes, FlightsAttachField.FareBags],
  }).map((group) => {
    const record = attachSubRecordOf({ value: group.open });
    return {
      seller: attachPartText({ record, key: FlightsAttachPart.Seller }),
      optionTitle: attachPartText({ record, key: FlightsAttachPart.Title }),
      fareType: attachPartText({ record, key: FlightsAttachPart.FareType }),
      priceAmount: attachPartNumber({ record, key: FlightsAttachPart.Price }),
      currency: attachPartText({ record, key: FlightsAttachPart.Currency }),
      includes: attachGroupValues({ group, key: FlightsAttachField.FareIncludes }),
      baggagePrices: attachGroupValues({ group, key: FlightsAttachField.FareBags }),
    };
  });
}

/**
 * One block item, read as a typed itinerary — or null when it carries no leg,
 * which is the one thing an itinerary cannot be without.
 *
 * THE HEADLINE IS NOT READ.
 */
export function readFlightsAttachItem(input: {
  item: AttachItemBlock;
}): FlightsAttachResult | null {
  const item = input.item;
  const segments = segmentsOf({ item });
  if (segments.length === 0) return null;
  return {
    priceAmount: fieldNumber({ item, key: FlightsAttachField.Price }),
    currency: fieldText({ item, key: FlightsAttachField.Currency }),
    tripType: fieldText({ item, key: FlightsAttachField.TripType }),
    totalDurationMinutes: fieldNumber({ item, key: FlightsAttachField.DurationMinutes }),
    stopsCount: fieldNumber({ item, key: FlightsAttachField.Stops }),
    segments,
    layovers: layoversOf({ item }),
    carbonKg: fieldNumber({ item, key: FlightsAttachField.CarbonKg }),
    carbonVsTypicalPct: fieldNumber({ item, key: FlightsAttachField.CarbonVsTypicalPct }),
    fares: faresOf({ item }),
  };
}
